import React, { useEffect, useMemo } from 'react';
import { useState } from 'react';
import { RadioGroup } from '@headlessui/react';
import {
  CheckCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/20/solid';
import {
  Form,
  Link,
  redirect,
  useActionData,
  useLoaderData,
  useNavigation,
} from 'react-router-dom';
import customFetch from '@utils/customFetch';
import { useCartContext } from '@context/cartContext';
import { CheckoutProvider } from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import CheckoutForm from '@components/bestellungen/CheckoutForm';
import AddressFields, {
  readAddressFields,
} from '@components/shared/AddressFields';
import getStripePromise from '@utils/stripePromise';

// Gespeicherte Liefer- und Rechnungsadresse zum Vorbefüllen. Ohne Login
// (401) bleibt das Formular leer — es ist dann ohnehin gesperrt.
export const loader = async () => {
  try {
    const { data } = await customFetch.get('/address');
    return data;
  } catch (error) {
    return { shippingAddress: null, billingAddress: null };
  }
};

export const action = async ({ request }) => {
  const formData = await request.formData();
  const data = Object.fromEntries(formData);
  const cart = JSON.parse(data.cart);

  const orderObject = {
    tax: data.tax,
    shippingFee: data.shippingFee,
    cartItems: cart,
    address: readAddressFields(data, 'shipping'),
    // Ohne abweichende Rechnungsadresse gilt serverseitig die Lieferadresse.
    ...(data.billingSameAsShipping === 'on'
      ? {}
      : { billingAddress: readAddressFields(data, 'billing') }),
    saveAddresses: data.saveAddresses === 'on',
  };

  try {
    const response = await customFetch.post('/orders', orderObject);
    return response;
  } catch (error) {
    toast.error(error?.response?.data?.msg);
    return error;
  }
};

const deliveryMethods = [
  {
    id: 1,
    title: 'Standardversand',
    turnaround: 'innerhalb von 2 Werktagen nach Versand',
    price: 500,
    default: true,
  },
  {
    id: 2,
    title: 'Expressversand',
    turnaround: 'am nächsten Werktag nach Versand',
    price: 1000,
    default: false,
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const OrderPage = () => {
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState(
    deliveryMethods[0].price,
  );

  const [orderId, setOrderId] = useState();
  const { shippingAddress, billingAddress } = useLoaderData();
  // Eine gespeicherte Rechnungsadresse heisst: sie weicht ab.
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(
    !billingAddress,
  );
  const { cart, total_amount, updateShipping } = useCartContext();

  // User from local storage
  const userObject = localStorage.getItem('user');
  let user;
  if (userObject) {
    user = JSON.parse(userObject);
    if (user.timestamp) {
      const storedTimestamp = new Date(user.timestamp);
      const currentTime = new Date();

      const timeDifference = currentTime - storedTimestamp;

      const oneDayInMs = 24 * 60 * 60 * 1000;

      if (timeDifference < oneDayInMs) {
        user.ok = true;
      } else {
        user.ok = false;
      }
    } else {
      console.log('Kein Timestamp gefunden.');
    }
  }

  useEffect(() => {
    updateShipping(selectedDeliveryMethod);
  }, [selectedDeliveryMethod]);

  const navigation = useNavigation();
  const isSubmitting = navigation.state === 'submitting';

  const [clientSecret, setClientSecret] = useState('');
  const actionData = useActionData();
  useEffect(() => {
    // Bei einem Fehler (z. B. unvollständige Adresse) liefert die Action den
    // Axios-Fehler zurück — dann bleibt das Formular stehen, der Toast
    // nennt den Grund.
    if (actionData?.data?.clientSecret) {
      setClientSecret(actionData.data.clientSecret);
      setOrderId(actionData.data.order._id);
    }
  }, [actionData]);

  // Stripe erst laden, wenn der Bezahlschritt erreicht ist (clientSecret gesetzt).
  const [stripe, setStripe] = useState(null);
  const [stripeError, setStripeError] = useState(false);
  useEffect(() => {
    if (!clientSecret) return;
    let active = true;
    getStripePromise()
      .then((stripeInstance) => {
        if (active) setStripe(stripeInstance);
      })
      .catch(() => {
        if (active) setStripeError(true);
      });
    return () => {
      active = false;
    };
  }, [clientSecret]);

  // Client-Secret der Checkout Session (POST /orders). CheckoutProvider
  // initialisiert genau einmal — die Optionen müssen stabil bleiben.
  const checkoutOptions = useMemo(
    () => ({
      fetchClientSecret: () => Promise.resolve(clientSecret),
      elementsOptions: { appearance: { theme: 'stripe' } },
    }),
    [clientSecret],
  );

  return (
    <main className="py-24">
      <div className="px-4 py-16 text-center sm:px-6 lg:px-8">
        <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
          Bestellung
        </h1>
      </div>

      <div className="mx-auto max-w-2xl px-4 pb-24 pt-16 sm:px-6 lg:max-w-7xl lg:px-8">
        <h2 className="sr-only">Bestellung</h2>
        {/* Hier Case Switch wenn Bezahlung initiiert */}
        {clientSecret ? (
          stripeError ? (
            <div className="mx-auto max-w-xl rounded-lg border border-gray-200 bg-white p-6 text-center shadow-xs">
              <p className="text-base text-gray-700">
                Die Zahlung konnte momentan nicht geladen werden. Bitte prüfe
                deine Internetverbindung und lade die Seite neu.
              </p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="mt-4 rounded-md border border-transparent bg-svisit-primary-600 px-4 py-3 text-base font-medium text-white shadow-xs hover:bg-svisit-primary-700 focus:outline-hidden focus:ring-2 focus:ring-svisit-primary-500 focus:ring-offset-2 focus:ring-offset-gray-50"
              >
                Seite neu laden
              </button>
            </div>
          ) : stripe ? (
            <CheckoutProvider options={checkoutOptions} stripe={stripe}>
              <CheckoutForm orderId={orderId} />
            </CheckoutProvider>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <svg
                className="h-8 w-8 animate-spin text-svisit-primary-600"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <p className="mt-4 text-base text-gray-600">
                Zahlung wird geladen, bitte warten...
              </p>
            </div>
          )
        ) : (
          <>
            {/* Anmeldung wenn schon Konto, weglassen wenn User vorhanden*/}
            {user?.ok === true ? (
              ''
            ) : (
              <div className="mb-4">
                <div className="rounded-md bg-blue-50 p-4 mt-4">
                  <div className="flex">
                    <div className="shrink-0">
                      <InformationCircleIcon
                        className="h-5 w-5 text-blue-400"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="ml-3 flex-1 md:flex md:justify-between">
                      <p className="text-sm text-blue-700">
                        Hast du schon ein Konto? Dann melde dich hier an, damit
                        du deine Bestellung abgeben kannst. Sonst kannst du
                        einfach ein neues Konto erstellen.
                      </p>
                      <p className="mt-3 text-sm md:ml-6 md:mt-0">
                        <Link
                          to="/login?redirectURL=/warenkorb/bestellung"
                          className="whitespace-nowrap font-medium text-blue-700 hover:text-blue-600"
                        >
                          Einloggen
                          <span aria-hidden="true"> &rarr;</span>
                        </Link>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <Form
              method="POST"
              className="lg:grid lg:grid-cols-2 lg:gap-x-12 xl:gap-x-16"
            >
              <div
                className={`${
                  user?.ok === true ? '' : 'opacity-50 cursor-not-allowed'
                }`}
              >
                <h2 className="text-lg font-medium text-gray-900">
                  Lieferadresse
                </h2>

                <div className="mt-4">
                  <AddressFields
                    section="shipping"
                    defaults={shippingAddress}
                    required={[
                      'firstName',
                      'lastName',
                      'address',
                      'city',
                      'country',
                      'zip',
                      'phone',
                    ]}
                    disabled={user?.ok === false}
                  />
                </div>

                <div className="mt-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <input
                      id="billingSameAsShipping"
                      name="billingSameAsShipping"
                      type="checkbox"
                      checked={billingSameAsShipping}
                      onChange={(e) =>
                        setBillingSameAsShipping(e.target.checked)
                      }
                      disabled={user?.ok === false}
                      className="h-4 w-4 rounded border-gray-300 text-svisit-primary-600 focus:ring-svisit-primary-500"
                    />
                    <label
                      htmlFor="billingSameAsShipping"
                      className="text-sm text-gray-700"
                    >
                      Rechnungsadresse entspricht der Lieferadresse
                    </label>
                  </div>
                </div>

                {!billingSameAsShipping && (
                  <div className="mt-10 border-t border-gray-200 pt-10">
                    <h2 className="text-lg font-medium text-gray-900">
                      Rechnungsadresse
                    </h2>
                    <div className="mt-4">
                      {/* Name ODER Firma genügt — das prüft der Server. */}
                      <AddressFields
                        section="billing"
                        defaults={billingAddress}
                        required={['address', 'city', 'country', 'zip']}
                        disabled={user?.ok === false}
                      />
                    </div>
                  </div>
                )}

                <div className="mt-6 flex items-center gap-3">
                  <input
                    id="saveAddresses"
                    name="saveAddresses"
                    type="checkbox"
                    defaultChecked
                    disabled={user?.ok === false}
                    className="h-4 w-4 rounded border-gray-300 text-svisit-primary-600 focus:ring-svisit-primary-500"
                  />
                  <label
                    htmlFor="saveAddresses"
                    className="text-sm text-gray-700"
                  >
                    Adressen für künftige Bestellungen speichern
                  </label>
                </div>

                <div className="mt-10 border-t border-gray-200 pt-10">
                  <RadioGroup
                    name="shippingFee"
                    value={selectedDeliveryMethod}
                    onChange={setSelectedDeliveryMethod}
                  >
                    <RadioGroup.Label className="text-lg font-medium text-gray-900">
                      Liefermethode
                    </RadioGroup.Label>

                    <div className="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                      {deliveryMethods.map((deliveryMethod) => (
                        <RadioGroup.Option
                          key={deliveryMethod.id}
                          value={deliveryMethod.price}
                          className={({ checked, active }) =>
                            classNames(
                              checked
                                ? 'border-transparent'
                                : 'border-gray-300',
                              active ? 'ring-2 ring-svisit-primary-500' : '',
                              'relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-xs focus:outline-hidden',
                            )
                          }
                        >
                          {({ checked, active }) => (
                            <>
                              <span className="flex flex-1">
                                <span className="flex flex-col">
                                  <RadioGroup.Label
                                    as="span"
                                    className="block text-sm font-medium text-gray-900"
                                  >
                                    {deliveryMethod.title}
                                  </RadioGroup.Label>
                                  <RadioGroup.Description
                                    as="span"
                                    className="mt-1 flex items-center text-sm text-gray-500"
                                  >
                                    {deliveryMethod.turnaround}
                                  </RadioGroup.Description>
                                  <RadioGroup.Description
                                    as="span"
                                    className="mt-6 text-sm font-medium text-gray-900"
                                  >
                                    CHF {deliveryMethod.price / 100}
                                  </RadioGroup.Description>
                                </span>
                              </span>
                              {checked ? (
                                <CheckCircleIcon
                                  className="h-5 w-5 text-svisit-primary-600"
                                  aria-hidden="true"
                                />
                              ) : null}
                              <span
                                className={classNames(
                                  active ? 'border' : 'border-2',
                                  checked
                                    ? 'border-svisit-primary-500'
                                    : 'border-transparent',
                                  'pointer-events-none absolute -inset-px rounded-lg',
                                )}
                                aria-hidden="true"
                              />
                            </>
                          )}
                        </RadioGroup.Option>
                      ))}
                    </div>
                  </RadioGroup>
                </div>
              </div>

              {/* Zusammenfassung */}
              <div className="mt-10 lg:mt-0">
                <h2 className="text-lg font-medium text-gray-900">
                  Zusammenfassung
                </h2>

                <div className="mt-4 rounded-lg border border-gray-200 bg-white shadow-xs">
                  <h3 className="sr-only">Items in your cart</h3>
                  <ul role="list" className="divide-y divide-gray-200">
                    {cart.map((product) => (
                      <li key={product.id} className="flex px-4 py-6 sm:px-6">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="h-20 w-20 flex-none rounded-md object-cover object-center"
                        />
                        <div className="flex-auto space-y-1 ml-3 text-sm font-medium">
                          <h3>{product.name}</h3>
                        </div>
                        <p className="flex-none text-base font-medium">
                          CHF {product.price / 100}
                        </p>
                      </li>
                    ))}
                  </ul>
                  <dl className="space-y-6 border-t border-gray-200 px-4 py-6 sm:px-6">
                    <div className="flex items-center justify-between">
                      <dt className="text-sm">Zwischentotal</dt>
                      <dd className="text-sm font-medium text-gray-900">
                        CHF {Math.round(total_amount) / 100}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-sm">Lieferkosten</dt>
                      <dd className="text-sm font-medium text-gray-900">
                        CHF {selectedDeliveryMethod / 100}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-sm">Enthaltene Mehrwertsteuer</dt>
                      <dd className="text-sm font-medium text-gray-900">
                        CHF{' '}
                        {Math.round(
                          (total_amount + selectedDeliveryMethod) * 0.081,
                        ) / 100}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between border-t border-gray-200 pt-6">
                      <dt className="text-base font-medium">Gesamtbetrag</dt>
                      <dd className="text-base font-medium text-gray-900">
                        CHF {(total_amount + selectedDeliveryMethod) / 100}
                      </dd>
                    </div>
                  </dl>

                  <input
                    type="hidden"
                    name="tax"
                    id="tax"
                    value={Math.round(
                      (total_amount + selectedDeliveryMethod) * 0.081,
                    )}
                  />

                  <input
                    type="hidden"
                    name="cart"
                    id="cart"
                    value={JSON.stringify(cart)}
                  />

                  <div className="border-t border-gray-200 px-4 py-6 sm:px-6">
                    <button
                      type="submit"
                      className="w-full rounded-md border border-transparent bg-svisit-primary-600 px-4 py-3 text-base font-medium text-white shadow-xs hover:bg-svisit-primary-700 focus:outline-hidden focus:ring-2 focus:ring-svisit-primary-500 focus:ring-offset-2 focus:ring-offset-gray-50 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      disabled={isSubmitting || user?.ok === false}
                    >
                      {isSubmitting
                        ? 'Du wirst gleich weitergeleitet...'
                        : 'Bestellen und zur Zahlung'}
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          </>
        )}
      </div>
    </main>
  );
};

export default OrderPage;
